import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Lock, RefreshCw, LogOut, Mail, Building2, FileText, MessageSquare, ExternalLink, CheckCircle2, XCircle } from "lucide-react";
import { LOGOS } from "../data";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const TOKEN_KEY = "ts_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ media_kit: 0, collaboration: 0, total: 0 });
  const [tab, setTab] = useState("all");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: "Bearer " + token };
        const [{ data: leadData }, { data: statData }] = await Promise.all([
          axios.get(`${API}/admin/leads`, { headers }),
          axios.get(`${API}/admin/stats`, { headers }),
        ]);
        if (cancelled) return;
        setLeads(leadData.items || []);
        setStats(statData);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
          toast.error("Session expired. Please sign in again.");
        } else {
          toast.error("Could not load leads.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [token, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Signed in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
  };

  const filtered = leads.filter((l) => tab === "all" || l.type === tab);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050708] text-white flex items-center justify-center px-5">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-[#061826] border border-white/8 rounded-sm p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <img src={LOGOS.emblem} alt="Traveler Submerged" className="h-10 w-auto" />
            <div>
              <div className="font-heading text-lg">Traveler Submerged</div>
              <div className="text-xs text-white/55 font-body">Admin access</div>
            </div>
          </div>
          <Label htmlFor="pw" className="text-[#CAF0F8]/80 text-xs uppercase tracking-widest">
            Password
          </Label>
          <Input
            id="pw"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            placeholder="Enter admin password"
            className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 focus-visible:ring-[#00B4D8]"
          />
          <Button
            type="submit"
            disabled={loggingIn || !password}
            data-testid="admin-login-btn"
            className="mt-5 w-full bg-[#00B4D8] text-[#050708] hover:bg-[#0077B6] hover:text-white disabled:opacity-60"
          >
            <Lock className="h-4 w-4 mr-2" />
            {loggingIn ? "Signing in..." : "Sign in"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050708] text-white">
      <header className="border-b border-white/8 bg-[#061826]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGOS.emblem} alt="" className="h-8 w-auto" />
            <span className="font-heading text-sm md:text-base text-white/90">Admin · Leads</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={refresh}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="text-white/80 hover:bg-white/5 hover:text-white"
            >
              <RefreshCw className={"h-4 w-4 mr-2 " + (loading ? "animate-spin" : "")} />
              Refresh
            </Button>
            <Button
              variant="ghost"
              onClick={logout}
              data-testid="admin-logout-btn"
              className="text-white/80 hover:bg-white/5 hover:text-white"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="grid grid-cols-3 gap-3 md:gap-5 mb-8">
          {[
            { label: "Total leads", v: stats.total },
            { label: "Media kit", v: stats.media_kit },
            { label: "Collaboration", v: stats.collaboration },
          ].map((s) => (
            <div key={s.label} className="bg-[#061826] border border-white/8 rounded-sm p-5 md:p-6">
              <div className="text-[10px] uppercase tracking-widest text-[#CAF0F8]/55">{s.label}</div>
              <div className="stat-num text-3xl md:text-4xl text-[#00B4D8] mt-2">{s.v}</div>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-[#061826] border border-white/8">
            <TabsTrigger value="all" data-testid="admin-tab-all">All</TabsTrigger>
            <TabsTrigger value="media_kit" data-testid="admin-tab-media-kit">Media kit</TabsTrigger>
            <TabsTrigger value="collaboration" data-testid="admin-tab-collab">Collaboration</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-6">
            {filtered.length === 0 ? (
              <div className="text-center py-20 text-white/55 font-body" data-testid="admin-empty">
                No leads yet.
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-leads-list">
                {filtered.map((l) => (
                  <LeadRow key={l.id} lead={l} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function LeadRow({ lead }) {
  const date = new Date(lead.created_at);
  const dateStr = date.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  const synced = lead.systeme_status === "synced";
  return (
    <div
      data-testid={"admin-lead-" + lead.id}
      className="bg-[#061826] border border-white/8 rounded-sm p-5 md:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className={
              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest " +
              (lead.type === "media_kit"
                ? "bg-[#00B4D8]/15 border border-[#00B4D8]/30 text-[#00B4D8]"
                : "bg-[#8FD6C8]/12 border border-[#8FD6C8]/30 text-[#8FD6C8]")
            }
          >
            {lead.type === "media_kit" ? <FileText className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}
            {lead.type === "media_kit" ? "Media kit" : "Collaboration"}
          </span>
          <span className="text-xs text-white/45 font-body">{dateStr}</span>
        </div>
        <span
          className={
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest " +
            (synced
              ? "bg-emerald-500/10 border border-emerald-400/30 text-emerald-300"
              : "bg-[#FF6B5E]/12 border border-[#FF6B5E]/30 text-[#FF6B5E]")
          }
        >
          {synced ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
          Systeme.io {lead.systeme_status}
          {lead.systeme_tagged && synced && " · tagged"}
        </span>
      </div>

      <div className="mt-4 grid md:grid-cols-3 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/45">Name</div>
          <div className="font-heading text-white text-base mt-1">{lead.name}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/45">Email</div>
          <a
            href={"mailto:" + lead.email}
            className="inline-flex items-center gap-1.5 mt-1 text-[#00B4D8] hover:underline text-sm break-all"
          >
            <Mail className="h-3.5 w-3.5" /> {lead.email}
          </a>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/45">Company</div>
          <div className="text-white/85 text-sm mt-1 flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 text-white/45" />
            {lead.company || <span className="text-white/40">-</span>}
          </div>
        </div>
      </div>

      {lead.message && (
        <div className="mt-4 pt-4 border-t border-white/8">
          <div className="text-[10px] uppercase tracking-widest text-white/45 mb-2">Brief</div>
          <p className="text-white/80 text-sm font-body whitespace-pre-wrap">{lead.message}</p>
        </div>
      )}

      {lead.systeme_contact_id && (
        <div className="mt-3 text-[10px] text-white/35 font-body flex items-center gap-1.5">
          <ExternalLink className="h-3 w-3" />
          Systeme.io contact ID: {lead.systeme_contact_id}
        </div>
      )}
    </div>
  );
}
